import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { find } from 'lodash';
import React, { useState } from 'react';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { phoneOnly } from '../../../../common/ConstantsStyles';
import { getFeatureFlag } from '../../../../common/Utils';
import { useLagoon } from '../../../../hooks/useLagoon';
import { StyledEducationContainerDiv } from '../MapView/CompareDrawer/utility/compareDrawer.styled';
import { getEducation } from '../MapView/CompareDrawer/utility/getEducationText';
import { StyledMarkdown } from '../MapView/Print/PrintView.style';
import {
  DividerStyle,
  LayoutGroupStyled,
  PremiumCareStyle,
} from './CompareSmartChoice.styled';
import { isAttributeChecked } from './utils';

export const Quality = ({
  selectedAttributes,
  isPremiumCareSuppressed,
  filteredProviders,
  isSmartChoice,
}) => {
  const mobile = useMediaQuery(phoneOnly);

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const enableLLMMedicalSpecialists: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LLM_MEDICAL_SPECIALISTS
  );
  const lagoonData = useLagoon('ui-messaging')();

  const service_data_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_SERVICE_DATA_AVAILABLE,
  });
  const no_service_data_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_NO_SERVICE_DATA_AVAILABLE,
  });
  const service_data_available_text = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_SERVICE_DATA_AVAILABLE_TEXT,
  });
  const no_service_data_available_text = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_NO_SERVICE_DATA_AVAILABLE_TEXT,
  });
  const credentialed_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_CREDENTIALED_PROVIDER,
  })?.message;
  const comparePCPText = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_QUALITY_PCP_TEXT,
  })?.message;
  const comparePCPSubText = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_QUALITY_PCP_SUBTEXT,
  })?.message;
  const compareNoPCPText = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_QUALITY_NO_PCP_TEXT,
  })?.message;
  const compareNoPCPSubText = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_QUALITY_NO_PCP_SUBTEXT,
  })?.message;
  const not_a_cred_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_NOT_A_CREDENTIALED_PROVIDER,
  })?.message;
  const edu_not_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_EDUCATION_INFO_NOT_AVAILABLE,
  })?.message;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const serviceDataPopoverContent = (provider) => (
    <Flex css={mobile ? { paddingLeft: '18px', paddingRight: '18px' } : {}}>
      <Text color={'#424242'} fontWeight={'400'} size={'14px'}>
        {provider?.recommendationDetails?.quality?.llmStatus === '1'
          ? service_data_available_text?.message
          : no_service_data_available_text?.message}
      </Text>
    </Flex>
  );

  const schoolName = (provider) => {
    const { schoolName } = getEducation(provider?.educationInfo || []);
    return schoolName;
  };
  const degreeCode = (provider) => {
    const { degreeCode } = getEducation(provider?.educationInfo || []);
    return degreeCode;
  };

  const noEducationInfo = (provider) => {
    const emptySchools = provider?.educationInfo?.find(
      (info) => info.schoolName === ''
    );

    return emptySchools ?? false;
  };

  const popoverTitle = (provider) =>
    provider?.recommendationDetails?.quality?.llmStatus === '1'
      ? service_data_available?.message
      : no_service_data_available?.message;
  return (
    <React.Fragment>
      {selectedAttributes?.find((item) => item?.name === 'premiumCareProvider')
        ?.isChecked &&
        isPremiumCareSuppressed === 'N' &&
        isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  {provider?.recommendationDetails?.quality
                    ?.premiumDesignation === 'Y' ? (
                    <div>
                      <Flex
                        alignItems="flex-start"
                        css={{ flexWrap: 'nowrap' }}
                      >
                        <IconSymbol
                          color="#002677"
                          css={{ marginTop: '2px' }}
                          icon="favorite"
                          size={18}
                        />
                        <IconSymbol
                          color="#002677"
                          css={{ marginTop: '2px' }}
                          icon="favorite"
                          size={18}
                        />
                        <StyledMarkdown
                          css={{ ...PremiumCareStyle, marginLeft: '8px' }}
                        >
                          {comparePCPText}
                        </StyledMarkdown>
                      </Flex>
                      <br></br>
                    </div>
                  ) : (
                    <div>
                      <StyledMarkdown css={PremiumCareStyle}>
                        {compareNoPCPText}
                      </StyledMarkdown>
                      <br></br>
                    </div>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  {provider?.recommendationDetails?.quality
                    ?.premiumDesignation === 'Y' ? (
                    <StyledMarkdown css={PremiumCareStyle}>
                      {comparePCPSubText}
                    </StyledMarkdown>
                  ) : (
                    <StyledMarkdown css={PremiumCareStyle}>
                      {compareNoPCPSubText}
                    </StyledMarkdown>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          </>
        )}

      {selectedAttributes?.find((item) => item?.name === 'premiumCareProvider')
        ?.isChecked &&
        !isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  {provider?.isPremiumCarePhysician === 'YES' ? (
                    <div>
                      <Flex
                        alignItems="flex-start"
                        css={{ flexWrap: 'nowrap' }}
                      >
                        <IconSymbol
                          color="#002677"
                          css={{ marginTop: '2px' }}
                          icon="favorite"
                          size={18}
                        />
                        <IconSymbol
                          color="#002677"
                          css={{ marginTop: '2px' }}
                          icon="favorite"
                          size={18}
                        />
                        <StyledMarkdown
                          css={{ ...PremiumCareStyle, marginLeft: '8px' }}
                        >
                          {comparePCPText}
                        </StyledMarkdown>
                      </Flex>
                      <br></br>
                    </div>
                  ) : (
                    <div>
                      <StyledMarkdown css={PremiumCareStyle}>
                        {compareNoPCPText}
                      </StyledMarkdown>
                      <br></br>
                    </div>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  {provider?.isPremiumCarePhysician === 'YES' ? (
                    <StyledMarkdown css={PremiumCareStyle}>
                      {comparePCPSubText}
                    </StyledMarkdown>
                  ) : (
                    <StyledMarkdown css={PremiumCareStyle}>
                      {compareNoPCPSubText}
                    </StyledMarkdown>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          </>
        )}

      {selectedAttributes?.find((item) => item?.name === 'credentials')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                {provider?.recommendationDetails?.quality?.credentialed ===
                'Y' ? (
                  <>
                    <IconSymbol color="#007000" icon="done" size={20} />
                    <Text css={{ marginLeft: '8px' }}>
                      {credentialed_provider}
                    </Text>
                  </>
                ) : (
                  <Text>{not_a_cred_provider}</Text>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          {isAttributeChecked(selectedAttributes, [
            'serviceData',
            'education',
          ]) && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {selectedAttributes?.find((item) => item?.name === 'education')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <React.Fragment key={index}>
                {!!noEducationInfo(provider) ? (
                  <StyledEducationContainerDiv
                    css={{ display: 'flex', alignItems: 'center' }}
                    data-testid="education-attribute"
                  >
                    <Text>{edu_not_available}</Text>
                  </StyledEducationContainerDiv>
                ) : (
                  <StyledEducationContainerDiv data-testid="education-attribute">
                    <Flex css={{ flexDirection: 'column' }}>
                      <Tooltip content={schoolName(provider)} position="top">
                        <Text color="$gray8" fontWeight={600} size="16px">
                          {schoolName(provider)}
                        </Text>
                      </Tooltip>
                      <Text color="$gray8" size="16px">
                        ({degreeCode(provider)})
                      </Text>
                    </Flex>
                  </StyledEducationContainerDiv>
                )}
              </React.Fragment>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%', minHeight: null } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {filteredProviders?.map((_, index) => (
              <React.Fragment key={index}>
                <DividerStyle />
              </React.Fragment>
            ))}
          </LayoutGroupStyled>
        </>
      )}

      <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
        {filteredProviders?.map((provider, index) => (
          <React.Fragment key={index}>
            {enableLLMMedicalSpecialists &&
              provider?.recommendationDetails?.quality?.llmStatus !== 'NA' && (
                <>
                  {selectedAttributes?.find(
                    (item) => item?.name === 'serviceData'
                  )?.isChecked && (
                    <>
                      <Flex data-testid="quality-breakdown-service-data">
                        {provider?.recommendationDetails?.quality?.llmStatus ===
                        '1'
                          ? service_data_available?.message
                          : no_service_data_available?.message}
                        <Drawer
                          data-testid="quality-breakdown-drawer"
                          isOpen={isDrawerOpen && mobile}
                          onClose={() => setIsDrawerOpen(!isDrawerOpen)}
                          position="bottom"
                          size="100"
                          title={popoverTitle(provider)}
                        >
                          {serviceDataPopoverContent(provider)}
                        </Drawer>
                        {mobile ? (
                          <IconSymbol
                            color="$interactive1"
                            css={{ marginLeft: '8px' }}
                            data-testid="quality-breakdown-drawer-icon-mobile"
                            icon="info"
                            onClick={() => setIsDrawerOpen(true)}
                            variant="outlined"
                          />
                        ) : (
                          <Popover
                            content={serviceDataPopoverContent(provider)}
                            data-testid="quality-breakdown-popover"
                            title={popoverTitle(provider)}
                          >
                            <IconSymbol
                              color="$interactive1"
                              css={{ marginLeft: '8px' }}
                              data-testid="quality-breakdown-popover-icon-desktop"
                              icon="info"
                              variant="outlined"
                            />
                          </Popover>
                        )}
                      </Flex>
                    </>
                  )}
                </>
              )}
          </React.Fragment>
        ))}
      </LayoutGroupStyled>
    </React.Fragment>
  );
};
